/**
 * MultiCvAppointmentList: US-D2-10（カタログDL完了画面等、複数CVを1つのポップアップ内にリスト表示）。
 * BR-U6-4: メインCVを優先表示（デザイン上の強調）。
 * BR-U6-1: 未ログイン時は各CVアイテムをモザイク表示する（アンケート回答経路・直アポ経路と統一）。
 *
 * <p>design-mockups/catalog-download-complete-tag-proposal.html（2026-08-06最終版）準拠
 * （2026-08-19デザイン更新）:
 * - 画面中央モーダル（背景暗転）に変更。メインCV/レコメンドCVをセクションで分離し、
 *   レコメンドセクションに見出し「一緒にダウンロードした資料」を追加。
 * - カード左端の縦線（「栞の紐」、メイン=太い/ブランドカラー、レコメンド=細い/グレー）で優先度を表現。
 * - レコメンドCVはグリッド表示（480px以下で1列）。
 * - 企業名の右に対応形式ラベルを表示。
 * - 確定ボタンは動的文言化・対面時リクエスト化。
 * - 日時選択・リクエスト展開は共通の中央カレンダーモーダル（calendarModalUi）で行う。
 * - 完了表示はカード内ロック＋ICS/Googleカレンダーリンク（企業名・コンテンツ名は残す）。
 */

import { createElement, createShadowHost, applyStylesheet, setText } from "../core/shadowDomUtil";
import { isLoggedIn } from "../core/authState";
import { submitSurveyAnswer } from "../w1-survey/submitSurveyAnswer";
import { submitAppointmentRequest } from "../w4-appointment/submitAppointmentRequest";
import { confirmAppointment } from "../w4-appointment/confirmAppointment";
import { requestAppointmentSlotsForAllCvs } from "../w4-appointment/requestAppointmentSlots";
import { showErrorMessageBanner } from "../core-ui/errorMessageBannerUi";
import { renderLoadingSpinner, LOADING_SPINNER_STYLES } from "../core-ui/loadingSpinnerUi";
import { LOGGED_OUT_MOSAIC_STYLES, renderLoggedOutMosaic } from "../core-ui/loggedOutMosaicUi";
import { renderMeetingTypeSelection } from "../w2-prebooking/meetingTypeSelectionUi";
import type { MeetingTypeSelectionResult } from "../w2-prebooking/meetingTypeSelectionUi";
import { renderMessageInput } from "../w2-prebooking/messageInputUi";
import type { MessageInputResult } from "../w2-prebooking/messageInputUi";
import { renderSlotSelectionSection, renderRequestSection, SLOT_FETCH_ERROR_MESSAGE, showDuplicateCompanyAppointmentError } from "../w2-prebooking/bookingSectionUi";
import { BOOKING_CARD_STYLES } from "../w2-prebooking/preBookingFormUi";
import { CONSENT_TEXT_STYLES } from "../core-ui/consentTextUi";
import { createWidgetPanel, buildWidgetLoginUrl, buildWidgetRegisterUrl, WIDGET_PANEL_STYLES } from "../core-ui/widgetPanelUi";
import { createCalendarModal, CALENDAR_MODAL_STYLES } from "../core-ui/calendarModalUi";
import type { CalendarModalHandle } from "../core-ui/calendarModalUi";
import { renderMeetingModeLabel, MEETING_MODE_LABEL_STYLES } from "../core-ui/meetingModeLabelUi";
import { renderCalendarAddActions, CALENDAR_ADD_ACTIONS_STYLES } from "../core-ui/calendarAddActionsUi";
import {
  DESIGN_TOKEN_CSS,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  TEXT_MUTED,
  BORDER_DEFAULT,
  SURFACE_SUBTLE,
  SURFACE_SELECTED,
  BRAND_PRIMARY,
  RADIUS_DEFAULT,
} from "../core-ui/designTokens";
import type { AppointmentSlot, ContentDisplayResult, SurveyChoice, PageEventPayload } from "../types";
import {
  trackSurveyAnswer,
  trackMeetingTypeSelect,
  trackSlotSelect,
  trackAppointmentSubmit,
  trackAppointmentSubmitFailed,
  trackWidgetMinimize,
} from "../w3-tracking/trackPageEvent";

const MULTI_CV_STYLES = `
:host { all: initial; ${DESIGN_TOKEN_CSS} }
${WIDGET_PANEL_STYLES}
${CALENDAR_MODAL_STYLES}
${MEETING_MODE_LABEL_STYLES}
${CALENDAR_ADD_ACTIONS_STYLES}
${BOOKING_CARD_STYLES}
${LOADING_SPINNER_STYLES}
${CONSENT_TEXT_STYLES}

.spica-proposal-section-label {
  font-size: 12px; font-weight: 700; letter-spacing: 0.02em; color: ${TEXT_SECONDARY};
  margin: 0 0 12px; padding-bottom: 8px; border-bottom: 2px solid ${BORDER_DEFAULT};
}
.spica-proposal-section { margin-bottom: 28px; }
.spica-proposal-section:last-child { margin-bottom: 0; }
.spica-proposal-main-grid { display: block; }
.spica-proposal-recommend-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
@media (max-width: 767px) {
  .spica-proposal-recommend-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 480px) {
  .spica-proposal-recommend-grid { grid-template-columns: 1fr; }
}

.spica-cv-item { position: relative; padding-left: 18px; margin-bottom: 12px; min-width: 0; }
.spica-proposal-recommend-grid .spica-cv-item { margin-bottom: 0; }
.spica-cv-item::before {
  content: ""; position: absolute; left: 0; top: 2px; bottom: 2px;
  width: 3px; border-radius: 2px; background: ${BORDER_DEFAULT};
}
.spica-cv-item.main-cv::before { width: 5px; background: ${BRAND_PRIMARY}; }
.spica-cv-body { background: #fff; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT}; padding: 18px 22px; position: relative; overflow: hidden; }
.spica-proposal-recommend-grid .spica-cv-body { height: 100%; box-sizing: border-box; }
.spica-cv-item.main-cv .spica-cv-body { border: 2px solid ${TEXT_PRIMARY}; padding: 24px 28px; }
.spica-cv-item.recommend-cv .spica-cv-body { padding: 14px 15px; }

.spica-company-name { font-size: 14px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 0 0 4px; display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 6px; }
.spica-cv-item.main-cv .spica-company-name { font-size: 16px; }
.spica-content-name { font-size: 12px; color: ${TEXT_SECONDARY}; margin: 0 0 10px; }
.spica-cv-item.main-cv .spica-content-name { font-size: 14px; margin-bottom: 16px; }

.spica-slot-list { list-style: none; margin: 0 0 10px; padding: 0; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 6px; }
.spica-slot-list li { display: flex; min-width: 0; }
.spica-slot-btn {
  box-sizing: border-box; width: 100%; padding: 7px 6px; border: 1px solid ${BORDER_DEFAULT};
  border-radius: ${RADIUS_DEFAULT}; background: #fff; font-size: 11px; color: ${TEXT_PRIMARY};
  cursor: pointer; text-align: center; white-space: nowrap; font-family: inherit;
  min-width: 0;
}
}
.spica-slot-btn:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }
.spica-slot-btn.selected { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; font-weight: 700; }
.spica-cv-item.main-cv .spica-slot-list { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.spica-slot-more-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 4px;
  border: 1px dashed #999999; background: #fff; color: ${TEXT_SECONDARY}; font-weight: 700;
}
.spica-slot-more-btn:hover { border-color: ${TEXT_PRIMARY}; color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }
.spica-cv-item.recommend-cv .spica-slot-btn { min-height: 36px; padding: 8px 6px; font-size: 11px; }
.spica-slot-expanded { margin-top: 4px; }
.spica-slot-back-btn { border: none; background: none; color: ${TEXT_PRIMARY}; font-size: 12px; font-weight: 700; cursor: pointer; padding: 0 0 12px; text-decoration: underline; }

.spica-calendar-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.spica-calendar-view { display: none; }
.spica-calendar-view.is-open { display: block; margin-top: 4px; }
.spica-calendar-nav-btn { border: 1px solid ${BORDER_DEFAULT}; background: #fff; width: 26px; height: 26px; border-radius: ${RADIUS_DEFAULT}; font-size: 12px; cursor: pointer; color: ${TEXT_PRIMARY}; }
.spica-calendar-nav-btn:hover { background: ${SURFACE_SUBTLE}; }
.spica-calendar-month-label { font-size: 12px; font-weight: 700; color: ${TEXT_PRIMARY}; }
.spica-calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; text-align: center; margin-bottom: 10px; }
.spica-calendar-weekday { font-size: 10px; color: #999999; padding-bottom: 3px; }
.spica-calendar-day { border: none; background: transparent; border-radius: ${RADIUS_DEFAULT}; padding: 5px 0; font-size: 11px; color: ${TEXT_PRIMARY}; cursor: pointer; }
.spica-calendar-day.has-slot { background: ${SURFACE_SUBTLE}; color: ${TEXT_PRIMARY}; font-weight: 700; }
.spica-calendar-day.has-slot:hover { background: ${SURFACE_SELECTED}; }
.spica-calendar-day.selected { background: ${TEXT_PRIMARY}; color: #fff; }
.spica-calendar-day.selected:hover { background: ${TEXT_PRIMARY}; color: #fff; }
.spica-calendar-day.disabled { color: #cccccc; cursor: default; }
.spica-calendar-day-slots { border-top: 1px solid ${BORDER_DEFAULT}; padding-top: 8px; margin-bottom: 10px; }
.spica-calendar-day-slots-label { font-size: 11px; color: ${TEXT_SECONDARY}; margin: 0 0 6px; }
.spica-calendar-slot-list { list-style: none; margin: 0; padding: 0 0 4px; display: flex; flex-wrap: nowrap; gap: 6px; overflow-x: auto; }
.spica-calendar-slot-list li { flex: 0 0 auto; }
.spica-calendar-slot-btn { box-sizing: border-box; flex: 0 0 auto; padding: 6px 10px; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT}; background: #fff; font-size: 11px; text-align: center; cursor: pointer; white-space: nowrap; font-family: inherit; color: ${TEXT_PRIMARY}; }
.spica-calendar-slot-btn:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }
.spica-calendar-slot-btn.selected { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; font-weight: 700; }
.spica-calendar-slot-btn:disabled { color: #cccccc; cursor: not-allowed; background: #fff; }
.spica-calendar-slot-btn:disabled:hover { border-color: ${BORDER_DEFAULT}; background: #fff; }

.spica-cv-question { font-size: 15px; font-weight: 700; margin: 0 0 14px; color: ${TEXT_PRIMARY}; line-height: 1.6; }
.spica-cv-choice-button {
  display: block; width: 100%; padding: 12px 14px; margin-bottom: 8px; border: 1px solid ${BORDER_DEFAULT};
  border-radius: ${RADIUS_DEFAULT}; background: #fff; cursor: pointer; text-align: left; font-size: 13px;
  color: ${TEXT_PRIMARY}; font-weight: 500; line-height: 1.5; font-family: inherit;
}
.spica-cv-choice-button:last-child { margin-bottom: 0; }
.spica-cv-choice-button:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }

.spica-cv-link-button {
  display: block; width: 100%; padding: 10px 20px; border: none; border-radius: ${RADIUS_DEFAULT};
  background: ${TEXT_PRIMARY}; color: #fff; cursor: pointer; text-align: center; font-size: 14px;
  font-weight: 700; font-family: inherit; text-decoration: none;
}

.spica-request-candidate-label { font-size: 11px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 10px 0 4px; }
.spica-request-candidate-label:first-of-type { margin-top: 0; }
.spica-request-candidate-value { font-size: 12px; color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT}; padding: 6px 8px; margin-bottom: 6px; cursor: pointer; }
.spica-request-candidate-value.empty { color: #999999; }
.spica-request-candidate-value.active { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; font-weight: 700; }
.spica-proposal-noslot { font-size: 13px; color: ${TEXT_PRIMARY}; margin: 0; font-weight: 700; }

.spica-meeting-type-group { display: flex; gap: 8px; margin-bottom: 12px; }
.spica-meeting-type-option {
  flex: 1; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT}; padding: 9px 8px;
  background: #fff; cursor: pointer; text-align: center; font-size: 12px; color: ${TEXT_PRIMARY}; font-family: inherit;
  font-weight: 500;
}
.spica-meeting-type-option:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }
.spica-meeting-type-option.selected { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; font-weight: 700; }
.spica-onsite-note {
  font-size: 11px; color: ${TEXT_SECONDARY}; background: ${SURFACE_SUBTLE}; border: 1px solid ${BORDER_DEFAULT};
  border-radius: ${RADIUS_DEFAULT}; padding: 8px 10px; margin: 0 0 12px; line-height: 1.6;
}

.spica-input-label { font-size: 11px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 0 0 4px; }
.spica-input-field {
  width: 100%; box-sizing: border-box; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT};
  padding: 8px 10px; font-size: 12px; color: ${TEXT_PRIMARY}; margin: 0 0 12px; resize: vertical; font-family: inherit;
}
.spica-input-field::placeholder { color: #999999; }
.spica-input-field:focus-visible { outline: 2px solid ${TEXT_PRIMARY}; outline-offset: 1px; }

.spica-request-toggle-btn {
  display: block; width: 100%; text-align: left; border: none; background: none;
  color: ${TEXT_PRIMARY}; font-size: 11px; font-weight: 700; cursor: pointer; padding: 6px 0 2px; font-family: inherit;
}
.spica-request-toggle-btn::before { content: "▸ "; }

/* 2026-09-30 追加: 日時候補確定後に通常リクエスト（空き枠選択）へ戻す控えめなテキストリンク。
   極力画面変更を抑えるため、アンダーラインなし・小さめ（10px）・淡色（TEXT_MUTED）とする。 */
.spica-request-back-link {
  display: inline-block; border: none; background: none; color: ${TEXT_MUTED};
  cursor: pointer; padding: 0; margin: 0 0 8px; font-family: inherit; font-size: 10px; line-height: 1.4; text-decoration: none;
}
.spica-request-back-link::before { content: "< "; }
.spica-request-back-link:hover { color: ${TEXT_SECONDARY}; }

.spica-onsite-request-note {
  margin: 6px 0 0; color: ${TEXT_SECONDARY}; font-size: 11px; line-height: 1.5;
}

.spica-proposal-candidate-value-static {
  font-size: 12px; color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; border: 1px solid ${BORDER_DEFAULT};
  border-radius: ${RADIUS_DEFAULT}; padding: 6px 8px; margin-bottom: 6px; cursor: pointer;
}
.spica-proposal-candidate-value-static:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; }

.spica-slot-label { font-size: 11px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 0 0 4px; }

.spica-cv-item.recommend-cv .spica-calendar-add-actions { display: grid; grid-template-columns: minmax(0, 1fr); }
.spica-cv-item.recommend-cv .spica-calendar-add-action { width: 100%; }
.spica-cv-item.recommend-cv .spica-calendar-ics-help { font-size: 10px; line-height: 1.5; margin-top: 6px; }

.spica-cv-body.is-discovery-compact > :not(.spica-company-name):not(.spica-content-name):not(.spica-discovery-entry) {
  display: none !important;
}
.spica-discovery-entry {
  display: none;
  width: 100%;
  min-height: 36px;
  margin-top: 12px;
  padding: 8px 10px;
  border: 1px solid ${TEXT_PRIMARY};
  border-radius: ${RADIUS_DEFAULT};
  background: #fff;
  color: ${TEXT_PRIMARY};
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-sizing: border-box;
}
.spica-cv-body.is-discovery-compact .spica-discovery-entry { display: block; }
.spica-discovery-entry:hover { background: ${SURFACE_SUBTLE}; }
.spica-discovery-entry:focus-visible { outline: 2px solid ${TEXT_PRIMARY}; outline-offset: 2px; }
.spica-cv-body.is-discovery-expanded { border-color: ${TEXT_PRIMARY}; }

.spica-cv-item.recommend-cv .spica-confirm-button { background: ${TEXT_PRIMARY}; font-size: 12px; padding: 9px; white-space: nowrap; }
.spica-cv-item.recommend-cv .spica-confirm-button:disabled { background: ${BORDER_DEFAULT}; color: #999999; }
.spica-cv-item.recommend-cv .spica-cv-body { padding: 14px 15px; }

@media (max-width: 767px) {
  .spica-cv-item.main-cv .spica-slot-list { grid-template-columns: 1fr 1fr; }
  .spica-proposal-recommend-grid > * { min-width: 0; }
  .spica-cv-item, .spica-cv-body { min-width: 0; }
  .spica-calendar-slot-list { min-width: 0; }
}
${LOGGED_OUT_MOSAIC_STYLES}
`;

export interface MultiCvAppointmentListCallbacks {
  onLoginRequired: (reason: "SURVEY_ANSWER") => void;
}

/**
 * コンテンツ名（カタログ名等）の表示用文字列を組み立てる。
 * resolveScenarioがcontentNameを返す場合は実名を表示し、未提供時は`objectType: objectRootId`にフォールバック。
 */
function buildContentDisplayName(result: ContentDisplayResult): string {
  return result.contentName ?? `${result.objectType}: ${result.objectRootId}`;
}


/** ISO8601日時文字列を「8/25(月) 15:00〜」形式に変換する（候補確定後の静的表示用）。 */
export async function renderMultiCvAppointmentList(
  results: ContentDisplayResult[],
  screenType: string
): Promise<void> {
  const visibleResults = results.filter((r) => r.displayMode !== "HIDDEN");
  const mainResults = visibleResults.filter((r) => r.isMainCv);
  const recommendResults = visibleResults.filter((r) => !r.isMainCv);

  const { host, shadowRoot } = createShadowHost("spica-multi-cv-appointment-list");
  applyStylesheet(shadowRoot, MULTI_CV_STYLES);

  // 効果分析ログ（イベントL、BR-AN-14）: 複数CVを1パネルにまとめて表示するため特定のCVには
  // 紐づかないが、companyRootId 欠落だとパイプラインの必須検証で保存されない（NFR-A3）。
  // 代表値としてメインCV（無ければ先頭の表示CV）の companyRootId を用いる（2026-09-29 レビュー反映）。
  const representativeCompanyRootId = (mainResults[0] ?? visibleResults[0])?.companyRootId;

  const panel = createWidgetPanel({
    position: "center-modal",
    lead: "ダウンロードした資料の掲載企業に直接話を聞いてみませんか？",
    ariaLabel: "ダウンロードした資料の掲載企業に直接話を聞いてみませんか？",
    minimizable: true,
    onMinimize: () => trackWidgetMinimize({ screenType, companyRootId: representativeCompanyRootId }),
  });
  shadowRoot.appendChild(panel.root);

  const calendarModal = createCalendarModal();
  shadowRoot.appendChild(calendarModal.element);

  // 空き枠取得中はスピナーを表示する
  const loadingEl = renderLoadingSpinner({ text: "空き日程を取得しています..." });
  panel.content.appendChild(loadingEl);
  document.body.appendChild(host);

  // FR-1（2026-09-11）: 未ログイン時は空き枠取得API（/api/public/available-slots）を呼ばない。
  // 各CVアイテムは renderCvListItem 内の isLoggedIn() 判定で固定モザイク（I261-77）を
  // 表示するため、未ログイン時は取得をスキップし空のスロットマップで描画へ進める。
  let availableSlotsByMemberGroup: Map<string, AppointmentSlot[]>;
  if (isLoggedIn()) {
    // 空き枠を非同期取得（ログイン済みのみ）
    const fetchResult = await requestAppointmentSlotsForAllCvs(visibleResults);
    loadingEl.remove();
    // 空き枠取得エラー時はエラーメッセージを表示して終了
    if (fetchResult.hasError) {
      panel.content.appendChild(createElement("p", { className: "spica-request-note", text: SLOT_FETCH_ERROR_MESSAGE }));
      return;
    }
    availableSlotsByMemberGroup = fetchResult.slotsByMemberGroup;
  } else {
    // 未ログイン: API未呼出。空のスロットマップで描画へ進める。
    loadingEl.remove();
    availableSlotsByMemberGroup = new Map<string, AppointmentSlot[]>();
  }

  if (mainResults.length > 0) {
    const mainSection = createElement("div", { className: "spica-proposal-section" });
    const mainGrid = createElement("div", { className: "spica-proposal-main-grid" });
    for (const result of mainResults) {
      mainGrid.appendChild(renderCvListItem(result, availableSlotsByMemberGroup, screenType, calendarModal, panel, true, true));
    }
    mainSection.appendChild(mainGrid);
    panel.content.appendChild(mainSection);
  }

  if (recommendResults.length > 0) {
    const recommendSection = createElement("div", { className: "spica-proposal-section" });
    recommendSection.appendChild(
      createElement("p", { className: "spica-proposal-section-label", text: "一緒にダウンロードした資料" })
    );
    const recommendGrid = createElement("div", { className: "spica-proposal-recommend-grid" });
    const recommendCards: HTMLElement[] = [];
    // I261-77: 未ログイン時のログイン導線（案内文＋「ログイン」「新規会員登録」の2ボタン）はメインCVに集約する。
    // ただしメインCVが無い（レコメンドCVだけが表示される）場合は、導線が1つも無くなるのを避けるため先頭のカードに出す。
    const loginActionOnFirstRecommend = mainResults.length === 0;
    for (const [index, result] of recommendResults.entries()) {
      const item = renderCvListItem(
        result,
        availableSlotsByMemberGroup,
        screenType,
        calendarModal,
        panel,
        false,
        loginActionOnFirstRecommend && index === 0
      );
      recommendCards.push(item);
      recommendGrid.appendChild(item);
    }
    recommendSection.appendChild(recommendGrid);
    panel.content.appendChild(recommendSection);

    // モック準拠: レコメンドCVは初期compact（企業名＋コンテンツ名＋「話を聞いてみる」ボタンのみ）。
    // クリックで展開し、他は閉じるアコーディオン方式。
    // I261-77: 未ログイン時は各カードに固定モザイク＋ログインボタンを最初から表示する（従来も各カードに
    // ログイン導線が最初から見えていたため、compact＋展開操作を挟まない）。ログイン済みは従来どおり。
    if (isLoggedIn()) {
      setupDiscoveryAccordion(recommendCards);
    }
  }
}

/**
 * CvListItem: displayModeに応じてSurveyQuestionView（SURVEY_AND_APPOINTMENT）
 * またはカレンダーグリッド（APPOINTMENT_ONLY）を内包する。
 * BR-U6-1: 未ログイン時はモザイク表示。メインCV/レコメンドCVは縦線の太さ・色で区別する。
 */
function renderCvListItem(
  result: ContentDisplayResult,
  availableSlotsByMemberGroup: Map<string, AppointmentSlot[]>,
  screenType: string,
  calendarModal: CalendarModalHandle,
  panel: ReturnType<typeof createWidgetPanel>,
  isMain: boolean,
  showLoginAction: boolean
): HTMLElement {
  const item = createElement("div", { className: isMain ? "spica-cv-item main-cv" : "spica-cv-item recommend-cv" });
  const body = createElement("div", { className: "spica-cv-body" });
  item.appendChild(body);

  const companyLabel = createElement("div", { className: "spica-company-name" });
  companyLabel.appendChild(document.createTextNode(result.companyName ?? `出展企業（${result.objectRootId}）`));
  // FR-2（2026-09-11）: 未ログイン時は商談形式ラベル（オンライン/対面）を実データで出さない
  // （blur越しに透けて見えるため）。企業名・コンテンツ名は企業詳細等で公開済みのため残す。
  if (isLoggedIn()) {
    const meetingModeLabel = renderMeetingModeLabel(result.meetingModePolicy);
    if (meetingModeLabel) companyLabel.appendChild(meetingModeLabel);
  }
  body.appendChild(companyLabel);

  body.appendChild(createElement("div", { className: "spica-content-name", text: buildContentDisplayName(result) }));

  // 効果分析ログ（BR-AN-1〜14）の共通文脈。取れる範囲の分析軸を各イベントへ付与する（BR-AN-8）。
  // このCV1件分の文脈。companyRootId は WIDGET_VIEW と同様にテナント境界確定のため付与する（NFR-A3）。
  // 未ログイン時のモザイク表示（LOGIN_PROMPT）にも必要なため、early return より前に組み立てる
  // （2026-09-29 レビュー反映）。sessionId は各 track ラッパーが付与する。
  const analyticsCtx: PageEventPayload = {
    screenType,
    objectType: result.objectType,
    objectRootId: result.objectRootId,
    companyRootId: result.companyRootId,
    prFlg: result.prFlg,
    isMainCv: result.isMainCv,
  };

  if (!isLoggedIn()) {
    // I261-77: 未ログイン時は日程調整画面ベースの固定モザイク（ダミー日程4マス＋案内文＋「ログイン」「新規会員登録」の2ボタン）を
    // カード（.spica-cv-body）の直接の子として1要素で追加する。従来のカード単位の暗幕＋白カード重畳は廃止。
    // LOGIN_PROMPT は従来どおり描画時にCVごと1回発火する（reason=SURVEY_ANSWER を維持）。
    // 発火は buildWidgetLoginUrl の呼出し時点なので、ログインボタンを出さないレコメンドCVでも必ず呼ぶ。
    // 案内文とログインボタンは、呼び出し元が指定したカード（メインCV。メインCVが無いときは先頭のレコメンドCV）にだけ出す。
    const loginUrl = buildWidgetLoginUrl("SURVEY_ANSWER", analyticsCtx);
    body.appendChild(renderLoggedOutMosaic({ loginUrl, registerUrl: buildWidgetRegisterUrl("SURVEY_ANSWER"), showLoginAction }));
    return item;
  }

  // 効果分析ログ（BR-AN-2、2026-09-29）: ファネル起点は WIDGET_VIEW（entry.ts）に一本化した
  // （WIDGET_OPEN は WIDGET_VIEW と発火が重複するため廃止）。ここでは起点イベントを発火しない。

  if (result.displayMode === "SURVEY_AND_APPOINTMENT" && result.survey) {
    renderCvSurveyQuestion(body, result, availableSlotsByMemberGroup, screenType, calendarModal, panel, analyticsCtx);
  } else {
    renderCvSlotSelection(body, result, availableSlotsByMemberGroup, screenType, calendarModal, panel, analyticsCtx);
  }

  return item;
}

function renderCvSurveyQuestion(
  container: HTMLElement,
  result: ContentDisplayResult,
  availableSlotsByMemberGroup: Map<string, AppointmentSlot[]>,
  screenType: string,
  calendarModal: CalendarModalHandle,
  panel: ReturnType<typeof createWidgetPanel>,
  analyticsCtx: PageEventPayload
): void {
  const surveyData = result.survey!;
  const questionEl = createElement("p", { className: "spica-cv-question", text: surveyData.question });
  container.appendChild(questionEl);

  const choicesWrapper = createElement("div");
  container.appendChild(choicesWrapper);

  for (const choice of surveyData.choices) {
    const button = createElement("button", { className: "spica-cv-choice-button" });
    setText(button, choice.label);
    button.addEventListener("click", () =>
      handleChoiceSelected(choice, result, container, choicesWrapper, questionEl, availableSlotsByMemberGroup, screenType, calendarModal, panel, analyticsCtx)
    );
    choicesWrapper.appendChild(button);
  }
}

async function handleChoiceSelected(
  choice: SurveyChoice,
  result: ContentDisplayResult,
  container: HTMLElement,
  choicesWrapper: HTMLElement,
  questionEl: HTMLElement,
  availableSlotsByMemberGroup: Map<string, AppointmentSlot[]>,
  screenType: string,
  calendarModal: CalendarModalHandle,
  panel: ReturnType<typeof createWidgetPanel>,
  analyticsCtx: PageEventPayload
): Promise<void> {
  // 効果分析ログ（イベントB、BR-AN-1）: アンケート選択肢を選んだ時点で発火する
  // （選択肢ID・アンケートIDを付与。選択肢ラベル等の自由記述は載せない、BR-AN-8）。
  trackSurveyAnswer({ ...analyticsCtx, surveyId: result.survey?.surveyId, selectedChoiceId: choice.id });

  // 全選択肢ボタンを非活性化（二重送信防止）
  const choiceButtons = choicesWrapper.querySelectorAll<HTMLButtonElement>(".spica-cv-choice-button");
  choiceButtons.forEach((btn) => { btn.disabled = true; });

  const surveyData = result.survey!;
  const submitResult = await submitSurveyAnswer({
    surveyId: surveyData.surveyId,
    scenarioId: result.scenarioId ?? "",
    objectType: result.objectType,
    objectRootId: result.objectRootId,
    selectedChoiceId: choice.id,
  });

  // ボタン復元（遷移しない場合のみ使用される）
  choiceButtons.forEach((btn) => { btn.disabled = false; });

  if (submitResult.requiresLogin) {
    panel.showLoginGate(buildWidgetLoginUrl("SURVEY_ANSWER", analyticsCtx));
    return;
  }
  if (submitResult.success && submitResult.action === "LINK" && submitResult.linkUrl) {
    questionEl.remove();
    choicesWrapper.textContent = "";
    const linkText = submitResult.linkDescription || submitResult.linkUrl;
    const link = createElement("a", {
      className: "spica-cv-link-button",
      text: linkText,
      attrs: { href: submitResult.linkUrl, target: "_blank", rel: "noopener noreferrer" },
    });
    choicesWrapper.appendChild(link);
    return;
  }
  if (submitResult.success && submitResult.action === "APPOINTMENT_OFFER") {
    questionEl.remove();
    choicesWrapper.remove();
    renderCvSlotSelection(container, result, availableSlotsByMemberGroup, screenType, calendarModal, panel, analyticsCtx, submitResult.surveyResponseLogId);
  }
}

function renderCvSlotSelection(
  container: HTMLElement,
  result: ContentDisplayResult,
  availableSlotsByMemberGroup: Map<string, AppointmentSlot[]>,
  screenType: string,
  calendarModal: CalendarModalHandle,
  panel: ReturnType<typeof createWidgetPanel>,
  analyticsCtx: PageEventPayload,
  surveyResponseLogId?: string
): void {
  const meetingTypeSelection = renderMeetingTypeSelection(result.meetingModePolicy, (meetingType) => {
    // 効果分析ログ（イベントC、BR-AN-1）: 商談形式（オンライン/対面）の切替を発火する
    // （HYBRID時のみ onChange が呼ばれる。ONLINE_ONLY/ON_SITE_ONLY は選択の余地がないため発火しない）。
    trackMeetingTypeSelect({ ...analyticsCtx, meetingType });
    if (messageInput) messageInput.updateMeetingType(meetingType);
    refreshConfirmButtonAction();
  });
  container.appendChild(meetingTypeSelection.element);

  const initialMeetingType = meetingTypeSelection.getSelectedMeetingType();

  let refreshConfirmButtonAction: () => void = () => {};
  let messageInput: MessageInputResult;

  const slots = result.memberGroupId ? availableSlotsByMemberGroup.get(result.memberGroupId) ?? [] : [];
  const card = container;

  if (slots.length === 0) {
    messageInput = renderMessageInput(initialMeetingType);

    renderRequestSection({
      container,
      panel,
      calendarModal,
      meetingTypeSelection,
      messageInput,
      companyName: result.companyName,
      contentName: result.contentName,
      memberGroupId: result.memberGroupId,
      onRequest: async (desiredDateTimes) => {
        if (!result.memberGroupId) return;
        void handleRequestAppointment(result, screenType, meetingTypeSelection, messageInput, panel, () => desiredDateTimes, card, calendarModal, analyticsCtx, surveyResponseLogId);
      },
    });
    return;
  }

  // 共通ロジック（bookingSectionUi）を使用してスロット選択セクションを構築する
  messageInput = renderMessageInput(initialMeetingType);

  refreshConfirmButtonAction = renderSlotSelectionSection({
    container,
    panel,
    slots,
    meetingTypeSelection,
    messageInput,
    calendarModal,
    companyName: result.companyName,
    contentName: result.contentName,
    memberGroupId: result.memberGroupId,
    // 効果分析ログ（イベントC、BR-AN-1）: 空き枠から日時候補を選択したとき SLOT_SELECT を発火する。
    onSlotSelectedForAnalytics: () => trackSlotSelect(analyticsCtx),
    onConfirm: async (slot) => {
      if (!result.memberGroupId) return;
      void handleConfirmAppointment(result, slot, screenType, meetingTypeSelection, messageInput, panel, card, calendarModal, analyticsCtx, surveyResponseLogId);
    },
    onRequest: async (desiredDateTimes) => {
      if (!result.memberGroupId) return;
      void handleRequestAppointment(result, screenType, meetingTypeSelection, messageInput, panel, () => desiredDateTimes, card, calendarModal, analyticsCtx);
    },
  });
}

/**
 * US-D2-04: 空き枠なし時のアポイントリクエスト送信。
 */
async function handleRequestAppointment(
  result: ContentDisplayResult,
  screenType: string,
  meetingTypeSelection: MeetingTypeSelectionResult,
  messageInput: MessageInputResult,
  panel: ReturnType<typeof createWidgetPanel>,
  getDesiredDateTimeValues: () => { desiredDateTime1: string; desiredDateTime2?: string; desiredDateTime3?: string } | null,
  card: HTMLElement,
  calendarModal: CalendarModalHandle,
  analyticsCtx: PageEventPayload,
  surveyResponseLogId?: string
): Promise<void> {
  if (!result.memberGroupId) return;

  const desiredDateTimes = getDesiredDateTimeValues();
  if (!desiredDateTimes) return;

  const { message, visitPreferredLocation } = messageInput.getValues();
  const submitResult = await submitAppointmentRequest({
    memberGroupId: result.memberGroupId,
    desiredDateTime1: desiredDateTimes.desiredDateTime1,
    desiredDateTime2: desiredDateTimes.desiredDateTime2,
    desiredDateTime3: desiredDateTimes.desiredDateTime3,
    screenType,
    objectType: result.objectType,
    objectRootId: result.objectRootId,
    scenarioId: result.scenarioId ?? undefined,
    meetingType: meetingTypeSelection.getSelectedMeetingType(),
    message,
    visitPreferredLocation,
    prFlg: result.prFlg,
    isRecommendCv: !result.isMainCv,
    surveyResponseLogId,
  });

  if (submitResult.requiresLogin) {
    // ログイン誘導は LOGIN_PROMPT（buildWidgetLoginUrl 集約）側で発火する。ここでは発火しない。
    calendarModal.close();
    panel.showLoginGate(buildWidgetLoginUrl("SURVEY_ANSWER", analyticsCtx));
    return;
  }
  if (submitResult.duplicateCompany) {
    // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（重複）。
    trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: submitResult.failureKind, businessErrorCode: submitResult.businessErrorCode });
    showDuplicateCompanyAppointmentError(submitResult.duplicateCompany.existingAppointmentId);
    return;
  }
  if (!submitResult.success) {
    // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（バリデーション/システム等）。
    trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: submitResult.failureKind, businessErrorCode: submitResult.businessErrorCode });
    return;
  }
  if (submitResult.success) {
    // 効果分析ログ（イベントG＝ゴール、BR-AN-1）: アポリクエスト送信成功（リクエスト）。
    trackAppointmentSubmit(analyticsCtx);
    lockCardAsCompleted(card, result, "アポイントリクエストを送信しました。", null);
    calendarModal.close();
  }
}

async function handleConfirmAppointment(
  result: ContentDisplayResult,
  slot: AppointmentSlot,
  screenType: string,
  meetingTypeSelection: MeetingTypeSelectionResult,
  messageInput: MessageInputResult,
  panel: ReturnType<typeof createWidgetPanel>,
  card: HTMLElement,
  calendarModal: CalendarModalHandle,
  analyticsCtx: PageEventPayload,
  surveyResponseLogId?: string
): Promise<void> {
  if (!result.memberGroupId) return;

  const { message, visitPreferredLocation } = messageInput.getValues();
  const confirmResult = await confirmAppointment({
    memberGroupId: result.memberGroupId,
    scheduledStartAt: slot.startAt,
    screenType,
    objectType: result.objectType,
    objectRootId: result.objectRootId,
    scenarioId: result.scenarioId ?? undefined,
    meetingType: meetingTypeSelection.getSelectedMeetingType(),
    message,
    visitPreferredLocation,
    prFlg: result.prFlg,
    isRecommendCv: !result.isMainCv,
    surveyResponseLogId,
  });

  if (confirmResult.requiresLogin) {
    // ログイン誘導は LOGIN_PROMPT（buildWidgetLoginUrl 集約）側で発火する。ここでは発火しない。
    panel.showLoginGate(buildWidgetLoginUrl("SURVEY_ANSWER", analyticsCtx));
    return;
  }
  if (confirmResult.slotUnavailable) {
    // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（空き枠なし）。
    trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: confirmResult.failureKind, businessErrorCode: confirmResult.businessErrorCode });
    showErrorMessageBanner({ errorType: "BUSINESS", message: "その枠は埋まりました。他の候補をお選びください。" });
    return;
  }
  if (confirmResult.duplicateCompany) {
    // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（重複）。
    trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: confirmResult.failureKind, businessErrorCode: confirmResult.businessErrorCode });
    showDuplicateCompanyAppointmentError(confirmResult.duplicateCompany.existingAppointmentId);
    return;
  }
  if (!confirmResult.success) {
    // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（システム等）。
    trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: confirmResult.failureKind, businessErrorCode: confirmResult.businessErrorCode });
    showErrorMessageBanner({ errorType: "SYSTEM", message: "エラーが発生しました。時間をおいて再度お試しください。" });
    return;
  }
  if (confirmResult.appointmentId) {
    // 効果分析ログ（イベントG＝ゴール、BR-AN-1）: アポリクエスト送信成功（確定）。appointmentId を載せる。
    trackAppointmentSubmit({ ...analyticsCtx, appointmentId: confirmResult.appointmentId });
    lockCardAsCompleted(card, result, "アポイントリクエストを送信しました。", {
      appointmentId: confirmResult.appointmentId,
      scheduledStartAt: confirmResult.scheduledStartAt,
      scheduledEndAt: confirmResult.scheduledEndAt,
      exhibitorCompanyName: confirmResult.exhibitorCompanyName,
    });
    calendarModal.close();
  }
}

/**
 * 予約確定・リクエスト送信完了後、カード（spica-cv-body）内を企業名・コンテンツ名を残したまま
 * 完了メッセージへ差し替える（design-mockups準拠）。
 */
function lockCardAsCompleted(
  card: HTMLElement,
  result: ContentDisplayResult,
  message: string,
  confirmed: { appointmentId: string; scheduledStartAt?: string; scheduledEndAt?: string; exhibitorCompanyName?: string | null } | null
): void {
  card.textContent = "";
  const companyLabel = createElement("div", { className: "spica-company-name" });
  companyLabel.appendChild(document.createTextNode(result.companyName ?? `出展企業（${result.objectRootId}）`));
  const meetingModeLabel = renderMeetingModeLabel(result.meetingModePolicy);
  if (meetingModeLabel) companyLabel.appendChild(meetingModeLabel);
  card.appendChild(companyLabel);
  card.appendChild(createElement("div", { className: "spica-content-name", text: buildContentDisplayName(result) }));
  card.appendChild(createElement("p", { className: "spica-proposal-noslot", text: message }));

  if (confirmed && confirmed.scheduledStartAt && confirmed.scheduledEndAt) {
    card.appendChild(
      renderCalendarAddActions({
        appointmentId: confirmed.appointmentId,
        scheduledStartAt: confirmed.scheduledStartAt,
        scheduledEndAt: confirmed.scheduledEndAt,
        exhibitorCompanyName: confirmed.exhibitorCompanyName,
      })
    );
  }
}

/**
 * レコメンドCVカードの折りたたみ/展開アコーディオン。
 * モック準拠: 初期は企業名＋コンテンツ名＋「話を聞いてみる」ボタンのみ表示。
 * ボタンクリックで展開し、他のカードは自動的に閉じる。
 */
function setupDiscoveryAccordion(items: HTMLElement[]): void {
  const bodies: HTMLElement[] = [];
  for (const item of items) {
    const body = item.querySelector(".spica-cv-body") as HTMLElement | null;
    if (!body) continue;
    bodies.push(body);

    // 「話を聞いてみる」ボタンを動的追加
    const entry = createElement("button", { className: "spica-discovery-entry", text: "話を聞いてみる" });
    entry.setAttribute("type", "button");
    entry.setAttribute("aria-expanded", "false");
    body.appendChild(entry);

    // 初期状態: compact
    body.classList.add("is-discovery-compact");

    entry.addEventListener("click", () => {
      // 他のカードをcompactに戻す
      for (const otherBody of bodies) {
        if (otherBody !== body) {
          otherBody.classList.remove("is-discovery-expanded");
          otherBody.classList.add("is-discovery-compact");
          const otherEntry = otherBody.querySelector(".spica-discovery-entry");
          if (otherEntry) otherEntry.setAttribute("aria-expanded", "false");
        }
      }
      // このカードを展開
      body.classList.remove("is-discovery-compact");
      body.classList.add("is-discovery-expanded");
      entry.setAttribute("aria-expanded", "true");
      body.scrollIntoView({ block: "nearest" });
    });
  }
}
